import type { ApolloLink } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import type { ToastLevel } from "@observe-tech/mfe-shell-api";

type Notify = (message: string, level: ToastLevel) => void;

export const OFFLINE_TOAST_DEDUPE_MS = 5_000;

// fetch rejects with a TypeError when the request never leaves the machine, and
// HttpLink passes that rejection through unwrapped, so it is the one signal that
// means the connection is down rather than the server disagreeing. Every other
// error link in the shell branches on an HTTP statusCode, which is why a dead
// link fails silently today while a 401 is handled. This link is installed
// directly in front of httpLink so it only ever sees what the transport
// produced: further up the chain it would also catch errors thrown by the
// auth-retry code, which Apollo's onError pipes to the upstream observer as a
// networkError. Only the cloud clients install this — the local marlin client
// reaches the on-site stack over the LAN, and its failures are a different
// problem with a different fix.
export function createOfflineToastLink(
  notify: Notify,
  getMessage: () => string,
  dedupeMs: number = OFFLINE_TOAST_DEDUPE_MS,
): ApolloLink {
  let lastToastAt = Number.NEGATIVE_INFINITY;

  return onError(({ networkError }) => {
    if (!(networkError instanceof TypeError)) return;

    const now = Date.now();
    if (now - lastToastAt < dedupeMs) return;
    lastToastAt = now;

    notify(getMessage(), "error");
  });
}
